import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'
import { AlertTriangle, AudioLines, Box, Clapperboard, FileText, Globe2, Image, Library, MapPin, Scissors, UserRound, Video, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { dataStr, type ReactFlowCanvasNode } from '../../hooks/useYCanvas'
import { cn } from '../../lib/utils'
import { createNadDebug } from '../../utils/debugLog'
import { useProjectId } from '../../contexts/ProjectIdContext'
import { usePaletteAnalytics } from '../../analytics/paletteAnalytics'
import { nodeSchemas, nodeTypes } from '../canvas-v2/nodeRegistry'
import { NodePresentationContext } from '../canvas-v2/NodePresentationContext'
import { SingleNodeDetailsProvider } from '../canvas-v2/nodes/shared/SingleNodeDetailsContext'
import { getSimplifiedCategory, type AssetCategory } from './simplifiedViewCategories'
import {
  LIST_COLUMN_MIN, LIST_PREVIEW_MAX_HEIGHT, clampListWidth, countGridColumns, fitListPreview,
  isHeightBoundedCategory, listWidthBounds, readStoredListWidth, writeStoredListWidth,
} from './simplifiedListLayout'
import { usePointerResize, type PointerResizeLogRecord } from '../../hooks/usePointerResize'

const log = createNadDebug('dw:simplified-view')
/** Horizontal padding of a list card slot (`p-3` on both sides). */
const SLOT_PADDING_X = 24
/** Quiet period before a card's fit is logged (see NodeListCard). */
const FIT_LOG_SETTLE_MS = 250
const listGridStyle: CSSProperties = {
  // `min(…, 100%)` lets the single track shrink when a classic scrollbar eats
  // into the 240px minimum pane, instead of overflowing horizontally.
  gridTemplateColumns: `repeat(auto-fit, minmax(min(${LIST_COLUMN_MIN}px, 100%), 1fr))`,
}
const CATEGORIES: ReadonlyArray<{ id: AssetCategory; icon: LucideIcon }> = [
  { id: 'script', icon: FileText },
  { id: 'character', icon: UserRound },
  { id: 'location', icon: MapPin },
  { id: 'object', icon: Box },
  { id: 'storyboard', icon: Clapperboard },
  { id: 'video', icon: Video },
  { id: 'sound', icon: AudioLines },
  { id: 'image', icon: Image },
  { id: 'timeline', icon: Scissors },
  { id: 'unsupported', icon: AlertTriangle },
]

interface SimplifiedWorkspaceProps {
  nodes: ReactFlowCanvasNode[]
  selectedNodeIds: ReadonlySet<string>
  onSelectNode: (id: string) => void
  onOpenLibrary: () => void
  overview: ReactNode
  /** The canvas's bottom palette (minus the hand/select picker), pinned bottom-centre. */
  palette: ReactNode
  /** Right-click on a list card or the detail preview: opens the node context menu. */
  onNodeContextMenu?: (event: ReactMouseEvent, nodeId: string) => void
}

/** The canonical registry provides both surfaces; no parallel node visual model. */
function ExistingNode({ node, selected }: { node: ReactFlowCanvasNode; selected: boolean }) {
  const Component = nodeTypes[node.type ?? 'default'] ?? nodeTypes.default
  // The canonical canvas instance owns initial uploads and its blob URL.
  // These extra presentations must never start another upload or revoke that URL.
  const data = useMemo(() => ({
    ...node.data,
    initialFile: undefined,
    _initialPreviewUrl: undefined,
  }), [node.data])
  return (
    <Component
      id={node.id}
      type={node.type ?? 'default'}
      data={data}
      selected={selected}
      dragging={false}
      isConnectable={false}
      xPos={node.position.x}
      yPos={node.position.y}
      zIndex={0}
    />
  )
}

function NodeListCard({ node, selected, onSelect, onContextMenu }: {
  node: ReactFlowCanvasNode
  selected: boolean
  onSelect: () => void
  onContextMenu?: (event: ReactMouseEvent) => void
}) {
  const { t } = useTranslation('workspace')
  const contentRef = useRef<HTMLDivElement>(null)
  const slotRef = useRef<HTMLDivElement>(null)
  // Native box = the list-presentation node as actually rendered (transform-
  // independent offset size), not ReactFlow's canvas measurement or a schema
  // guess — aspect-keyed nodes (video 9:16 vs 16:9) render different widths.
  const [measured, setMeasured] = useState({ nativeW: 0, nativeH: 0, boxW: 232 })
  const boxH = isHeightBoundedCategory(getSimplifiedCategory(node.type)) ? LIST_PREVIEW_MAX_HEIGHT : null
  const { nativeW, nativeH, boxW } = measured
  const { scale, bound } = fitListPreview({ nativeW, nativeH, boxW, boxH })
  const label = dataStr(node.data, 'in_name') || dataStr(node.data, 'name')
    || nodeSchemas[node.type ?? '']?.label || node.type || node.id

  useLayoutEffect(() => {
    const slot = slotRef.current
    const content = contentRef.current
    if (!slot || !content) return
    const measure = () => {
      const next = {
        nativeW: content.offsetWidth,
        nativeH: content.offsetHeight,
        boxW: Math.max(1, slot.clientWidth - SLOT_PADDING_X),
      }
      setMeasured((previous) => (
        previous.nativeW === next.nativeW && previous.nativeH === next.nativeH && previous.boxW === next.boxW
          ? previous
          : next
      ))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(slot)
    observer.observe(content)
    return () => observer.disconnect()
  }, [node.id])

  // Settled + change-only: the box width changes on every pointermove of a
  // divider drag, so only log once the fit has been stable for FIT_LOG_SETTLE_MS.
  const fitKey = nativeW > 0
    ? `${Math.round(nativeW)}x${Math.round(nativeH)}@${Math.round(boxW)}x${boxH ?? '-'}:${scale.toFixed(3)}`
    : null
  useEffect(() => {
    if (fitKey === null) return
    const timer = window.setTimeout(() => log({
      event: 'simplified-view:list-card-fit', feature: 'simplified-view', node_id: node.id, node_type: node.type ?? 'default',
      native_w: Math.round(nativeW), native_h: Math.round(nativeH), box_w: Math.round(boxW), box_h: boxH,
      scale: Number(scale.toFixed(3)), bound,
    }), FIT_LOG_SETTLE_MS)
    return () => window.clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey])

  return (
    <div role="listitem" className="relative min-w-0" data-list-node-id={node.id} data-testid={`simplified-row-${node.id}`} onContextMenu={onContextMenu}>
      <div
        ref={slotRef}
        className={cn('relative rounded-xl border p-3', selected ? 'border-primary bg-surface-2' : 'border-transparent')}
      >
        {/* Sized to the scaled box; overflow-hidden because transform does not
            shrink the unscaled layout box, which would otherwise overflow. */}
        <div className="mx-auto overflow-hidden" style={{ width: Math.ceil(nativeW * scale), height: Math.ceil(nativeH * scale) }}>
          <div
            ref={contentRef}
            // Inert prevents a card's inputs/media from becoming a second editor.
            // A sibling button supplies keyboard selection and an accessible name.
            {...{ inert: '' }}
            aria-hidden="true"
            className="pointer-events-none w-max origin-top-left pt-8"
            style={{ transform: `scale(${scale})` }}
          >
            <NodePresentationContext.Provider value="list">
              <SingleNodeDetailsProvider selectedNodeCount={0}>
                <ExistingNode node={node} selected={false} />
              </SingleNodeDetailsProvider>
            </NodePresentationContext.Provider>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onSelect}
        aria-label={t('simplifiedView.selectNode', { defaultValue: 'Select {{name}}', name: label })}
        aria-pressed={selected}
        className="absolute inset-0 rounded-xl border border-transparent hover:border-input-border-hover hover:bg-surface-2/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      />
    </div>
  )
}

const detailStyle: CSSProperties & { '--canvas-viewport-zoom': number } = {
  '--canvas-viewport-zoom': 1,
}

/**
 * Node-list pane width. `preferredWidth` is the user's dragged choice (persisted);
 * the rendered width re-clamps it to the live root width so a temporarily narrow
 * window never overwrites the preference and the detail pane keeps its minimum.
 */
function useResizableNodeList(listVisible: boolean) {
  const rootRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [preferredWidth, setPreferredWidth] = useState(readStoredListWidth)
  const [rootWidth, setRootWidth] = useState<number | null>(null)
  const rootWidthRef = useRef<number | null>(null)
  const columnsRef = useRef(0)
  const movedRef = useRef(false)
  const bounds = listWidthBounds(rootWidth)
  const width = clampListWidth(preferredWidth, bounds)

  useLayoutEffect(() => {
    const root = rootRef.current
    if (!root) return
    const measure = () => {
      rootWidthRef.current = root.clientWidth
      setRootWidth(root.clientWidth)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(root)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const list = listRef.current
    if (!listVisible || !list) return
    // Grid reflow is CSS-driven; this only observes it for the NAD trace.
    const report = () => {
      const columns = countGridColumns(list)
      if (columns === columnsRef.current) return
      columnsRef.current = columns
      log({ event: 'simplified-view:list-columns-changed', feature: 'simplified-view', width: list.offsetWidth, columns })
    }
    report()
    const observer = new ResizeObserver(report)
    observer.observe(list)
    return () => {
      observer.disconnect()
      columnsRef.current = 0
    }
  }, [listVisible])

  const getBounds = useCallback(() => listWidthBounds(rootWidthRef.current), [])
  const onWidthChange = useCallback((next: number) => {
    movedRef.current = true
    setPreferredWidth(next)
  }, [])
  // Persist once per drag, and only if the pointer actually moved: a bare click
  // on the divider in a narrow window must not overwrite the saved preference
  // with the temporarily clamped width.
  const onResizeEnd = useCallback((finalWidth: number) => {
    if (movedRef.current) writeStoredListWidth(finalWidth)
    movedRef.current = false
  }, [])
  const resizeLog = useCallback((record: PointerResizeLogRecord) => {
    const { event, ...fields } = record
    const endFields = event === 'resize-end' ? {
      ...listWidthBounds(rootWidthRef.current),
      width: fields.endWidth,
      columns: listRef.current ? countGridColumns(listRef.current) : null,
    } : {}
    log({ ...fields, ...endFields, event: `simplified-view:list-${event}`, feature: 'simplified-view' })
  }, [])
  const { onPointerDown } = usePointerResize({
    width,
    onWidthChange,
    getBounds,
    disabled: !listVisible,
    disabledReason: 'list-hidden',
    log: resizeLog,
    onResizeEnd,
  })

  return { rootRef, listRef, width, bounds, onPointerDown }
}

export function SimplifiedWorkspace({
  nodes, selectedNodeIds, onSelectNode, onOpenLibrary, overview, palette, onNodeContextMenu,
}: SimplifiedWorkspaceProps) {
  const { t } = useTranslation('workspace')
  const projectId = useProjectId()
  const selectedNode = selectedNodeIds.size === 1
    ? nodes.find((node) => selectedNodeIds.has(node.id) && getSimplifiedCategory(node.type) !== null)
    : undefined
  const selectedCategory = selectedNode ? getSimplifiedCategory(selectedNode.type) : null
  const [category, setCategory] = useState<AssetCategory | 'overview' | null>(selectedCategory)
  const selectedId = selectedNode?.id

  useEffect(() => {
    // Includes selection changes made by chat mentions, Duplicate, and Add Node.
    if (selectedId) setCategory(selectedCategory)
  }, [selectedId, selectedCategory])

  const grouped = useMemo(() => {
    const result = new Map<AssetCategory, ReactFlowCanvasNode[]>()
    for (const node of nodes) {
      const group = getSimplifiedCategory(node.type)
      if (group !== null) {
        const entries = result.get(group) ?? []
        entries.push(node)
        result.set(group, entries)
      }
    }
    return result
  }, [nodes])
  const listedNodes = category && category !== 'overview' ? grouped.get(category) ?? [] : []
  const listVisible = category !== 'overview'
  const nodeList = useResizableNodeList(listVisible)
  const nodeListId = useId()
  const detailNode = category !== 'overview' && selectedCategory === category ? selectedNode : undefined

  const analytics = usePaletteAnalytics()
  const chooseCategory = (next: AssetCategory | 'overview') => {
    analytics?.instant('list_rail', 'category', 'pick', { option: next })
    setCategory(next)
    log({ event: 'simplified-view:category-changed', feature: 'simplified-view', project_id: projectId, category: next })
  }
  // The rail button only ever opens the library (no toggle), so start an
  // interaction only when the library is not already owned by one.
  const openLibraryFromRail = () => {
    if (!analytics?.active('asset_library')) analytics?.open('list_rail', 'asset_library')
    onOpenLibrary()
  }
  const railButtonClass = (active: boolean) => cn(
    'flex w-full flex-col items-center justify-center gap-1 rounded-lg border px-1 py-2 text-xxs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
    active ? 'border-input-border bg-surface-2 text-foreground' : 'border-transparent text-muted-foreground hover:border-input-border-hover hover:bg-surface-2 hover:text-foreground',
  )

  return (
    <div ref={nodeList.rootRef} className="absolute inset-0 flex min-w-0 bg-background" data-testid="simplified-workspace">
      <nav aria-label={t('simplifiedView.assetTypes', 'Asset types')} className="flex w-20 shrink-0 flex-col gap-1 overflow-y-auto border-r border-border-subtle bg-surface-0 px-2 pt-3 pb-20">
        <button type="button" className={railButtonClass(category === 'overview')} aria-pressed={category === 'overview'} onClick={() => chooseCategory('overview')} data-testid="simplified-category-overview">
          <Globe2 className="h-4 w-4" />
          <span className="text-xxs leading-tight">{t('simplifiedView.overview', 'Overview')}</span>
        </button>
        {CATEGORIES.filter(({ id }) => id !== 'unsupported' || grouped.has(id)).map(({ id, icon: Icon }) => (
          <button key={id} type="button" className={railButtonClass(category === id)} aria-pressed={category === id} onClick={() => chooseCategory(id)} data-testid={`simplified-category-${id}`}>
            <Icon className="h-4 w-4" />
            <span className="text-xxs leading-tight">{t(`simplifiedView.categories.${id}`)}</span>
          </button>
        ))}
        <button type="button" className={railButtonClass(false)} onClick={openLibraryFromRail} data-testid="simplified-asset-library">
          <Library className="h-4 w-4" />
          <span className="text-xxs leading-tight">{t('simplifiedView.assetLibrary', 'Asset Library')}</span>
        </button>
      </nav>
      {listVisible && (
        <>
          <div
            ref={nodeList.listRef}
            className="grid shrink-0 content-start items-start gap-2 overflow-y-auto border-r border-border-subtle bg-surface-0 px-2 pt-3 pb-20"
            style={{ ...listGridStyle, width: nodeList.width }}
            id={nodeListId}
            role="list"
            aria-label={t('simplifiedView.nodes', 'Nodes')}
            data-testid="simplified-node-list"
          >
            {listedNodes.map((node) => <NodeListCard key={node.id} node={node} selected={node.id === detailNode?.id} onSelect={() => onSelectNode(node.id)} onContextMenu={onNodeContextMenu ? (event) => onNodeContextMenu(event, node.id) : undefined} />)}
            {category && listedNodes.length === 0 && <p className="col-span-full px-3 py-4 text-sm text-text-muted">{t('simplifiedView.noNodes', 'No nodes of this type yet.')}</p>}
          </div>
          {/* A flex sibling, not an overlay, so it can never shadow the list scrollbar. */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-controls={nodeListId}
            aria-label={t('simplifiedView.resizeNodeList', 'Resize node list')}
            aria-valuenow={nodeList.width}
            aria-valuemin={nodeList.bounds.min}
            aria-valuemax={nodeList.bounds.max}
            onPointerDown={nodeList.onPointerDown}
            className="w-1.5 shrink-0 cursor-col-resize touch-none transition-colors hover:bg-chrome/20"
            data-testid="simplified-node-list-resize-handle"
          />
        </>
      )}
      <section
        aria-label={t('simplifiedView.preview', 'Node preview')}
        data-testid="simplified-node-preview"
        className="min-w-0 flex-1 overflow-auto px-6 pt-6 pb-24"
      >
        {category === 'overview' ? overview : detailNode && (
          <div key={detailNode.id} className="mx-auto w-full max-w-4xl" style={detailStyle} data-preview-node-id={detailNode.id} onContextMenu={onNodeContextMenu ? (event) => onNodeContextMenu(event, detailNode.id) : undefined}>
            <NodePresentationContext.Provider value="detail">
              <SingleNodeDetailsProvider selectedNodeCount={1}>
                <ExistingNode node={detailNode} selected />
              </SingleNodeDetailsProvider>
            </NodePresentationContext.Provider>
          </div>
        )}
      </section>
      {/* bottom-11 matches the canvas palette's 44px offset, so the bar
          stays put when switching views. */}
      <div className="absolute bottom-11 left-1/2 z-50 -translate-x-1/2">
        {palette}
      </div>
    </div>
  )
}
